document.addEventListener('DOMContentLoaded', function() {
  const addToCartForms = document.querySelectorAll('form[action="/cart/add"]');

  function getFormProductData(form) {
    const productJson = form.querySelector('[data-product-json]');

    if (productJson) {
      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        console.error('Unable to parse product variant data:', error);
      }
    }

    return window.product || null;
  }

  function getVariantSelectors(form) {
    return Array.from(form.querySelectorAll('.variant-select'));
  }

  function getSelectedOptions(form) {
    return getVariantSelectors(form).map(select => select.value);
  }

  function getOptionButtons(form) {
    return Array.from(form.querySelectorAll('[data-option-button]'));
  }

  function getVariantButtons(form) {
    return Array.from(form.querySelectorAll('[data-variant-button]'));
  }

  function findVariantForOptions(product, selectedOptions) {
    if (!product || !Array.isArray(product.variants) || selectedOptions.length === 0) return null;

    return product.variants.find(variant => {
      return JSON.stringify(variant.options) === JSON.stringify(selectedOptions);
    }) || null;
  }

  // Item 15: is `selectedOptions` still worth offering up to `upToIndex`, i.e. does some
  // AVAILABLE variant still match the values chosen so far? Only the options BEFORE the
  // one being tested constrain the answer. That is deliberate: if every option
  // constrained every other one, a shopper who landed on a pair whose alternatives are
  // all sold out would find every value in every row disabled and no way back out.
  // Constraining forwards only leaves the first row always selectable, so every
  // combination stays reachable.
  function findAvailableVariantForOptions(product, selectedOptions, upToIndex) {
    if (!product || !Array.isArray(product.variants)) return null;

    return product.variants.find(variant => {
      if (!variant.available || !Array.isArray(variant.options)) return false;

      for (let index = 0; index <= upToIndex; index += 1) {
        const value = selectedOptions[index];
        if (value && variant.options[index] !== value) return false;
      }

      return true;
    }) || null;
  }

  function findSelectedVariant(form) {
    const product = getFormProductData(form);
    const variantSelectors = getVariantSelectors(form);

    // Where the form picks options rather than whole variants, the option selectors are
    // the source of truth. The hidden id input still holds the PREVIOUS combination when
    // this runs from a change handler, so reading it first would return a stale variant
    // on every change after the first.
    if (variantSelectors.length) {
      return findVariantForOptions(product, getSelectedOptions(form));
    }

    const variantInput = form.querySelector('input[name="id"]');

    if (variantInput && variantInput.value && product && Array.isArray(product.variants)) {
      return product.variants.find(variant => String(variant.id) === String(variantInput.value)) || null;
    }

    return null;
  }

  function syncOptionButtons(form, variant) {
    const product = getFormProductData(form);
    const selectedOptions = getSelectedOptions(form);

    getOptionButtons(form).forEach(button => {
      const optionPosition = Number(button.dataset.optionPosition || 1);
      const optionIndex = optionPosition - 1;
      const optionValue = button.dataset.optionValue;
      const buttonOptions = selectedOptions.slice();
      buttonOptions[optionIndex] = optionValue;
      const selected = selectedOptions[optionIndex] === optionValue;
      // Was `buttonVariant && !buttonVariant.available`, which only ever fired once every
      // option was answered -- so on load a sold-out size looked exactly like a stocked
      // one. Ask instead whether any available variant is still reachable through this
      // value, which is true from the first render onwards.
      const disabled = !findAvailableVariantForOptions(product, buttonOptions, optionIndex);

      // The Find spells sold-out sizes out in the label ("8 — Sold out") rather than
      // relying on colour alone. Opt in per button, since the sizes are the same values
      // on every row and the suffix has to come and go as the selection changes.
      const soldOutSuffix = button.dataset.soldOutSuffix;
      if (soldOutSuffix) {
        button.textContent = disabled ? optionValue + soldOutSuffix : optionValue;
      }

      button.classList.toggle('is-selected', selected);
      button.classList.toggle('w--ecommerce-pill-selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.classList.toggle('is-disabled', disabled);
      button.classList.toggle('w--ecommerce-pill-disabled', disabled);
      if (disabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    });
  }

  // Writing submitButton.textContent wipes every child of the button. That is fine for a
  // plain text button, but it destroyed any structured layout inside it -- e.g. a button
  // laid out as "ADD TO CART        287 GBP" with the price in its own span, which is what
  // The Find's spec asks for. Setting only a [data-add-label] span when one is present
  // keeps that structure intact, and buttons without the span fall back to the old
  // behaviour untouched. The labels themselves are overridable per button, since they were
  // previously hardcoded here and so could not differ between sections.
  function setSubmitLabel(submitButton, text) {
    const labelElement = submitButton.querySelector('[data-add-label]');
    if (labelElement) {
      labelElement.textContent = text;
    } else {
      submitButton.textContent = text;
    }
    // Kept for both element types, as before: <input type="submit"> renders its value,
    // and a <button name="add"> submits its value with the form. Only the textContent
    // write is now conditional, so nothing else about the old behaviour changes.
    submitButton.value = text;
  }

  function setSubmitState(form, variant) {
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    if (!submitButton) return;

    const labelAvailable = submitButton.dataset.labelAvailable || 'ADD TO BAG';
    const labelSoldOut = submitButton.dataset.labelSoldOut || 'SOLD OUT';

    if (!variant) {
      submitButton.disabled = true;
      setSubmitLabel(submitButton, labelAvailable);
      return;
    }

    const available = Boolean(variant.available);
    submitButton.disabled = !available;
    setSubmitLabel(submitButton, available ? labelAvailable : labelSoldOut);
  }

  // The Find renders its price as "287 GBP" -- the localised currency code, no decimals --
  // not the shop's money format, and it used to read that string off the clicked variant
  // button. With one button per OPTION there is no per-variant button to read, so the
  // section ships the same strings as a variant id -> label map. Sections that still
  // render whole-variant buttons keep working through the first branch.
  function getVariantPriceLabel(form, variant) {
    const selectedVariantButton = form.querySelector(`[data-variant-button][data-variant-id="${variant.id}"]`);
    if (selectedVariantButton?.dataset.variantPrice) return selectedVariantButton.dataset.variantPrice;

    const priceMap = form.querySelector('[data-variant-price-map]');
    if (priceMap) {
      try {
        const labels = JSON.parse(priceMap.textContent);
        if (labels && labels[variant.id]) return labels[variant.id];
      } catch (error) {
        console.error('Unable to parse variant price labels:', error);
      }
    }

    return formatMoney(variant.price);
  }

  function updateVariantDisplay(form, variant) {
    if (!variant) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    const priceElement = form.querySelector('.current-price') || document.querySelector('.current-price');
    if (priceElement) priceElement.textContent = getVariantPriceLabel(form, variant);

    syncOptionButtons(form, variant);
    getVariantButtons(form).forEach(button => {
      const selected = String(button.dataset.variantId) === String(variant.id);
      button.classList.toggle('is-selected', selected);
      button.classList.toggle('selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
    setSubmitState(form, variant);
  }

  function showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.textContent = message || 'This item is not available right now.';
      errorElement.hidden = false;
      return;
    }

    console.error(message || 'Unable to add item to cart.');
  }

  addToCartForms.forEach(form => {
    const variantSelectors = getVariantSelectors(form);

    if (variantSelectors.length) {
      const initialVariant = findSelectedVariant(form);
      if (initialVariant) {
        updateVariantDisplay(form, initialVariant);
      } else {
        syncOptionButtons(form, null);
        setSubmitState(form, null);
      }

      variantSelectors.forEach(selector => {
        selector.addEventListener('change', function() {
          const variant = findSelectedVariant(form);
          if (variant) {
            updateVariantDisplay(form, variant);
          } else {
            // Half-answered, or a combination the merchant never created. Clear the
            // resolved id so a stale one from the previous combination can never be
            // submitted, and put the button back to its unselected state.
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput) variantInput.value = '';
            syncOptionButtons(form, null);
            setSubmitState(form, null);
          }
        });
      });

      getOptionButtons(form).forEach(button => {
        button.addEventListener('click', function() {
          if (button.getAttribute('aria-disabled') === 'true') return;

          const optionPosition = Number(button.dataset.optionPosition || 1);
          const selector = form.querySelector(`.variant-select[data-option-position="${optionPosition}"]`);
          if (!selector) return;

          selector.value = button.dataset.optionValue;
          selector.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    getVariantButtons(form).forEach(button => {
      button.addEventListener('click', function() {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = button.dataset.variantId || '';

        const priceElement = form.querySelector('.current-price') || document.querySelector('.current-price');
        if (priceElement && button.dataset.variantPrice) priceElement.textContent = button.dataset.variantPrice;

        const variant = findSelectedVariant(form);
        if (variant) updateVariantDisplay(form, variant);
      });
    });

    form.addEventListener('submit', function(e) {
      e.preventDefault();

      const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
      const variant = findSelectedVariant(form);
      const variantInput = form.querySelector('input[name="id"]');
      const errorElement = form.querySelector('[data-add-to-cart-error]');

      if (errorElement) errorElement.hidden = true;

      if (variant) {
        updateVariantDisplay(form, variant);
      }

      if (!variantInput || !variantInput.value) {
        showAddToCartError(form, 'Please select an available size.');
        return;
      }

      if (variant && !variant.available) {
        showAddToCartError(form, 'That size is sold out.');
        return;
      }

      if (submitButton) submitButton.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantInput.value),
            quantity: 1
          }]
        })
      })
      .then(response => response.json().then(data => ({ ok: response.ok, data })))
      .then(result => {
        if (!result.ok || result.data.status === 422) {
          throw new Error(result.data.description || 'Unable to add item to cart.');
        }

        return fetch('/cart.js');
      })
      .then(response => response.json())
      .then(cart => {
        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: cart
        }));

        if (window.updateCart) window.updateCart();
        if (window.openCartDrawer) {
          window.openCartDrawer();
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      })
      .catch(error => {
        showAddToCartError(form, error.message);
      })
      .finally(() => {
        const latestVariant = findSelectedVariant(form);
        if (submitButton) submitButton.disabled = latestVariant ? !latestVariant.available : true;
      });
    });
  });
});

function formatMoney(cents) {
  const moneyFormat = window.theme && window.theme.settings && window.theme.settings.moneyFormat
    ? window.theme.settings.moneyFormat
    : (window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}');

  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, moneyFormat);
  }

  const locale = typeof Shopify !== 'undefined' && Shopify.locale ? Shopify.locale : 'en-US';
  const currency = typeof Shopify !== 'undefined' && Shopify.currency ? Shopify.currency.active : 'USD';

  return (cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency
  });
}
